import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippy.js/react';

import CapabilityHelper from 'helpers/newCapabilityHelper';
import { getTimeSinceDateString } from 'helpers/dateHelper';
import { IconAssetOnline, IconAssetOffline } from 'svgIcons/MotionPortalIcons';
import { LoadingStatusWrapper } from 'sharedComponents';
import { getAssetIcon } from 'helpers/icons';
import { MAX_WORDS, MAX_TAGS, assetOnlineStatus } from './constants';
import './style.scss';
import TabsV2 from '../../PowertrainAssetDetails/DetailedInformation/TabsV2';
import HeaderComponent from '../../PowertrainAssetDetails/DetailedInformation/Header/HeaderComponent';
import { NotificationStatusBar } from '../../PowertrainAssetDetails/DetailedInformation/NotificationStatusBar';
import { iconCondition } from '../ConditionIndicesOverviewV2/helpers';
import { getAssetTypeId } from 'helpers/assetAndPowertrainFilterHelper';
import { ASSET_TYPES } from 'helpers/constants';
import useUserCapability from 'sharedHooks/useUserCapability';

const AssetOverviewDetailsComponent = ({ actions, model }) => {
    const { t: translate } = useTranslation();
    const { selectedAsset, selectedAssetV2, newSelectedAssetCapability, productTypeCode, assetLabels, assetOnline, connectivityType, subscriptionLoadingStatus, lastStatusChangeRecievedTimestamp, nameplatePicturesLoadingStatus, selectedNotification } = model;
    const subscriptionType = model?.subscriptionStatus?.[0]?.subscriptionInfos?.[0]?.type ?? '-';
    const newCapabilityHelper = new CapabilityHelper(newSelectedAssetCapability, { id: selectedAsset?.id });
    const conditionBasedClassName = iconCondition(selectedAssetV2?.condition);
    const tagOptions = assetLabels?.slice(0, MAX_TAGS);
    const userCapabilities = useUserCapability();
    const maxWords = MAX_WORDS;

    const handleDoubleClick = useCallback((e) => {
        e.preventDefault();
        const span = e.target.closest('span[data-value]');
        const selection = document.getSelection();
        selection.removeAllRanges();
        if (span) {
            const range = document.createRange();
            range.selectNodeContents(span);
            selection.addRange(range);
        }
    }, []);

    const tagContent = tagOptions?.map((tag) => {
        if (tag.tagValue.length <= maxWords) {
            return <li className='asset-overview-v2__tag' key={tag.tagId}>{tag.tagValue}</li>;
        } else {
            return <li className='asset-overview-v2__tag' key={tag.tagId}>
                <Tippy
                    content={tag.tagValue}
                    placement='bottom-end'
                    theme='light-border'>
                    <span>{tag.tagValue.slice(0, maxWords) + '...'}</span>
                </Tippy>
            </li>;
        }
    });

    useEffect(() => {
        const id = selectedAsset?.id;
        if (id) {
            const assetIdParams = { assetIds: [id], type: null, statusCodes: [], maxEndDate: null };
            actions.getSubscriptionInfo(assetIdParams);
            actions.getApiAssetCapability({ id: id });
            actions.getAssetOverview(id);
            actions.getAssetLabels(id);
            actions.getSmartConditionSummary(id);
            if (getAssetTypeId(selectedAsset?.assetType) === ASSET_TYPES.DRIVE &&
                userCapabilities.hasDriveEventsSummary()) {
                actions.getDriveEventsSummary(id);
            }
        }
    }, [selectedAsset?.id]);

    const renderAssetOnline = () => {
        return (
            <Tippy
                className='info-button-with-online-help-tippy'
                interactive={true}
                content={
                    <div>
                        <div className='asset-overview-v2__online-text'>
                            {assetOnline === assetOnlineStatus.ONLINE ?
                                translate('ABB.Powertrain.Frontend.assetOverviewAssetConnectedSinceLabel') : lastStatusChangeRecievedTimestamp ?
                                    translate('ABB.Powertrain.Frontend.assetOverviewAssetDisconnectedSinceLabel') : translate('ABB.Powertrain.Frontend.assetOverviewAssetDisconnectedLabel')}
                        </div>
                        {lastStatusChangeRecievedTimestamp ? <div className='asset-overview-v2__last-measurement'>
                            {translate('ABB.Powertrain.Frontend.lastStatusUpdate')} {getTimeSinceDateString(lastStatusChangeRecievedTimestamp)}
                        </div> : null}
                    </div>
                }
                interactiveBorder={10}
                theme='light-border'
                trigger='mouseenter click'
                placement='auto'
                maxWidth='400px'
                popperOptions={{
                    modifiers: {
                        flip: {
                            enabled: true
                        }
                    }
                }}
            >
                <button className={`asset-overview-v2__asset ${assetOnline === assetOnlineStatus.ONLINE ? 'asset-overview-v2__asset-online' : 'asset-overview-v2__asset-offline'} `}>
                    {assetOnline === assetOnlineStatus.ONLINE ? <IconAssetOnline /> : <IconAssetOffline />}
                </button>
            </Tippy>
        );
    };

    return (
        <LoadingStatusWrapper loadingStatus={[subscriptionLoadingStatus]}>
            <NotificationStatusBar coloredIcon={true} />
            <div className='asset-overview-v2'>
                <div className={`asset-overview-v2__header ${conditionBasedClassName}`}>
                    <div className='asset-overview-v2__header-row'>
                        <div className='d-flex w-100'>
                            <h1 className='asset-overview-v2__heading' onDoubleClick={handleDoubleClick}>
                                <span className='asset-overview-v2__asset-icon'>
                                    {getAssetIcon(selectedAsset?.assetType, { width: 20, height: 20 })}
                                </span>
                                <span className='asset-overview-v2__heading-name' title={selectedAsset?.name} data-value>{selectedAsset?.name}</span>
                            </h1>
                            {renderAssetOnline()}
                        </div>
                        <HeaderComponent
                            showLegacyDetails={false}
                            asset={selectedAsset}
                            nameplatePicturesLoadingStatus={nameplatePicturesLoadingStatus}
                            selectedNotification={selectedNotification} />
                    </div>
                    <ul className='asset-overview-v2__details' onDoubleClick={handleDoubleClick}>
                        <li>{translate('ABB.Powertrain.Frontend.assetOverviewAssetIDLabel')}: <span data-value>{selectedAsset?.id}</span></li>
                        <li>{translate('ABB.Powertrain.Frontend.assetOverviewSerialNumberLabel')}: <span data-value>{selectedAsset?.serialNumber ?? '-'}</span></li>
                        <li>{translate('ABB.Powertrain.Frontend.assetOverviewProductTypeCodeLabel')}: <span data-value>{productTypeCode?.productType ?? '-'}</span></li>
                        <li>{translate('ABB.Powertrain.Frontend.assetOverviewSubscriptionLabel')}: <span data-value>{subscriptionType}</span></li>
                        <li>{translate('ABB.Powertrain.Frontend.assetOverviewConnectivityType')}: <span data-value>{connectivityType || translate('ABB.Powertrain.Frontend.assetOverviewNoGateway')}</span></li>
                    </ul>
                    <div className='asset-overview-v2__connection-type' />
                    {newCapabilityHelper.hasLabelingCapability() && <ul className='asset-overview-v2__tags'>
                        {tagContent}
                    </ul>}
                </div>
                <TabsV2 selectedAsset={selectedAsset} newCapability={newCapabilityHelper} selectedAssetV2={selectedAssetV2} conditionBasedClassName={conditionBasedClassName} />
            </div>
        </LoadingStatusWrapper>
    );
};


AssetOverviewDetailsComponent.propTypes = {
    actions: PropTypes.shape({
        getSubscriptionInfo: PropTypes.func,
        getApiAssetCapability: PropTypes.func,
        getAssetOverview: PropTypes.func,
        getAssetLabels: PropTypes.func
    }),
    selectedAsset: PropTypes.shape({
        assetId: PropTypes.number,
        assetSerialNumber: PropTypes.string,
        name: PropTypes.string,
        assetType: PropTypes.string,
        conditionIndex: PropTypes.object,
        condition: PropTypes.string,
        limits: PropTypes.object,
        threshold: PropTypes.object,
        trend: PropTypes.object,
    }),
    model: PropTypes.shape({
        allAssets: PropTypes.array,
        newSelectedAssetCapability: PropTypes.object,
        productTypeCode: PropTypes.object,
        assetLabels: PropTypes.array,
        assetOnline: PropTypes.string,
        connectivityType: PropTypes.string,
        subscriptionLoadingStatus: PropTypes.string,
        lastStatusChangeRecievedTimestamp: PropTypes.string,
        assetDetailsLabelsLoadingStatus: PropTypes.string,
        subscriptionStatus: PropTypes.array,
        selectedAsset: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            serialNumber: PropTypes.string,
        })
    })
};

export default AssetOverviewDetailsComponent;
